import React from 'react';
import { Form, useActionData, useLoaderData, useSubmit } from 'react-router-dom';

import { Await } from '@oysterjs/ui/async';
import { Spinner } from '@oysterjs/ui/spinner';
import { ActionResult } from '@oysterjs/core/graphql/actions';

import {
  Action,
  Resource,
  SubscriptionEntitlement,
  useGetTeamsQuery,
  type AgencyProfileFieldsFragment,
  type CustomPresetFieldsFragment,
  type UpdateAgencyProfileMutation
} from '~/api/graphql';
import { useAgencyUsers } from '~/components/current-user';
import { useEntitlements } from '~/components/entitlements';
import {
  pendingChangesSaveMessages,
  PendingChangesFormHeader,
  PendingChangesSaveBar,
  PendingChangesSaveToast,
  usePendingChangesSave
} from '~/components/settings/pending-changes-save';
import { PermissionNotice, usePermissions } from '~/components/permissions';
import {
  agencyProfileToFormInput,
  formInputToAgencyProfileInput,
  type AgencyProfileAssignment,
  type AgencyProfileFormInput,
  type CapabilityCatalog
} from '~/components/settings/agency-profile-form-input';
import {
  AgencyProfileAccessSection,
  AgencyProfileAssignmentSection,
  AgencyProfileBasicInformationSection,
  AgencyProfileHomeScreenSection
} from '~/components/settings/agency-profile-sections';

const UPDATE_AGENCY_PROFILE = [{ resource: Resource.AgencyProfile, action: Action.Update }];

const agencyProfileSaveMessages = {
  successTitle: 'Profile saved',
  successDescription: 'Your changes have been saved and applied to everyone on this profile.',
  errorTitle: 'Error saving profile',
  errorDescriptionPrefix: 'There was an error saving:'
} satisfies typeof pendingChangesSaveMessages.voiceAgent;

const AgencyProfileDetailLoader = () => (
  <div className="rounded-lg bg-white dark:bg-neutral-800 px-6 shadow">
    <div className="py-12 text-center">
      <Spinner size="size-8" color="neutral" />
      <h3 className="mt-2 text-sm font-semibold text-neutral-900 dark:text-white">
        Loading profile...
      </h3>
      <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
        This might take a moment.
      </p>
    </div>
  </div>
);

export const SettingsAgencyProfileDetailPage = () => {
  const loader = useLoaderData() as {
    data: Promise<{
      agencyProfile: AgencyProfileFieldsFragment;
      agencyProfileCapabilityCatalog: CapabilityCatalog;
      customPresets: CustomPresetFieldsFragment[];
      agencyProfileAssignments: AgencyProfileAssignment[];
    }>;
  };

  return (
    <React.Suspense fallback={<AgencyProfileDetailLoader />}>
      <Await resolve={loader.data}>
        {(data) =>
          data?.agencyProfile && (
            <AgencyProfileSettingsForm
              initialProfile={data.agencyProfile}
              catalog={data.agencyProfileCapabilityCatalog}
              customPresets={data.customPresets ?? []}
              agencyProfileAssignments={data.agencyProfileAssignments ?? []}
            />
          )
        }
      </Await>
    </React.Suspense>
  );
};

const AgencyProfileSettingsForm = ({
  initialProfile,
  catalog,
  customPresets,
  agencyProfileAssignments
}: {
  initialProfile: AgencyProfileFieldsFragment;
  catalog: CapabilityCatalog;
  customPresets: CustomPresetFieldsFragment[];
  agencyProfileAssignments: AgencyProfileAssignment[];
}) => {
  const action = useActionData() as ActionResult<UpdateAgencyProfileMutation>;
  const submit = useSubmit();
  const canUpdate = usePermissions().hasAllPermissions(UPDATE_AGENCY_PROFILE);
  const { hasEntitlement } = useEntitlements();
  const hasProAccess = hasEntitlement(SubscriptionEntitlement.CopilotProPresets);
  const agencyUsers = useAgencyUsers();

  // Teams carry their full membership, which the assignment section needs to
  // work out who a team already covers.
  const { data: teamsData } = useGetTeamsQuery({ fetchPolicy: 'cache-and-network' });
  const teams = React.useMemo(
    () => (teamsData?.teams ?? []).filter((team) => !team.archivedAt),
    [teamsData?.teams]
  );

  const [form, setForm] = React.useState<AgencyProfileFormInput>(() =>
    agencyProfileToFormInput(initialProfile)
  );

  const { changesPending, saved, error, discardChanges, markPendingChanges } =
    usePendingChangesSave({
      initialSavedState: React.useMemo(
        () => agencyProfileToFormInput(initialProfile),
        [initialProfile]
      ),
      action,
      saveSuccessTrigger: action?.data?.updateAgencyProfile?.updatedAt,
      isSaveSuccessful: (saveAction) => !!saveAction?.data?.updateAgencyProfile?.id,
      getSavedStateAfterCommit: () => agencyProfileToFormInput(action!.data!.updateAgencyProfile!),
      onAfterSave: (savedState) => setForm(savedState)
    });

  const updateForm = React.useCallback(
    (updater: (prev: AgencyProfileFormInput) => AgencyProfileFormInput) => {
      setForm(updater);
      markPendingChanges();
    },
    [markPendingChanges]
  );

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    submit(formInputToAgencyProfileInput(form), { method: 'PUT', encType: 'application/json' });
  };

  const sectionProps = { form, updateForm };

  return (
    <Form className="space-y-6" onSubmit={handleSubmit}>
      <PendingChangesSaveToast saved={saved} error={error} messages={agencyProfileSaveMessages} />
      {canUpdate ? (
        <PendingChangesSaveBar
          changesPending={changesPending}
          onDiscard={() => discardChanges(setForm)}
        />
      ) : (
        <PermissionNotice>
          You can see how this profile is configured but not change it.
        </PermissionNotice>
      )}
      <PendingChangesFormHeader
        title={`${form.name || 'Untitled'} Profile`}
        description="Configure what users assigned to this profile can see and do."
        backHref="/app/settings/access-profiles"
        changesPending={changesPending}
      />
      <AgencyProfileBasicInformationSection {...sectionProps} />
      <AgencyProfileAssignmentSection
        {...sectionProps}
        profileId={initialProfile.id}
        teams={teams}
        agencyUsers={agencyUsers}
        agencyProfileAssignments={agencyProfileAssignments}
      />
      <AgencyProfileHomeScreenSection
        {...sectionProps}
        customPresets={customPresets}
        hasProAccess={hasProAccess}
      />
      <AgencyProfileAccessSection {...sectionProps} catalog={catalog} />
    </Form>
  );
};
